import React, { useMemo } from "react";
import { Box, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { VIEWS } from "../consts";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    backgroundColor: theme.palette.grey.paper,
    paddingInlineStart: theme.spacing(2),
    paddingBlock: theme.spacing(1.2)
  },
  tabsContainer: {
    display: "flex",
    position: "relative",
    width: "100%"
  },
  tab: {
    flex: 1,
    textTransform: "none",
    borderRadius: theme.shape.borderRadius,
    fontWeight: 400,
    padding: theme.spacing(0.75, 1),
    minWidth: "133px",
    color: theme.palette.grey.toggleGrey,
    backgroundColor: theme.palette.blue.babyLight,
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: "2px"
    }
  },
  activeTab: {
    backgroundColor: "#90CAF9",
    color: "#48485D",
    border: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: theme.palette.primary.light
    }
  },
  leftButton: {
    left: 5,
    zIndex: 100
  },
  rightButton: {
    right: 5,
    zIndex: 100
  }
}));

/**
 * TabNavigation component for switching between Highlights and Answers views
 *
 * @param {Object} props - Component props
 * @param {string} props.activeMode - Current active view mode
 * @param {Function} props.onModeChange - Handler for changing the view mode
 * @param {Function} props.handleCombinedViewToggle - Handler for toggling combined view
 * @param {boolean} props.combinedViewMode - Whether combined view is active
 * @returns {React.ReactElement} - Rendered component
 */
const TabNavigation = ({
  activeMode,
  onModeChange,
  handleCombinedViewToggle,
  combinedViewMode
}) => {
  const classes = useStyles();

  // Determine active state using memoization to prevent unnecessary recalculations
  const { isHighlightsActive, isAnswersActive } = useMemo(() => {
    const highlightsActive = [
      VIEWS.HIGHLIGHTS,
      VIEWS.READER,
      VIEWS.CARDS
    ].includes(activeMode);
    const answersActive =
      activeMode === VIEWS.ANSWERS || activeMode === VIEWS.COMBINED;

    return {
      isHighlightsActive: highlightsActive,
      isAnswersActive: answersActive
    };
  }, [activeMode]);

  // Handler functions with explicit responsibility
  const handleHighlightsClick = () => {
    // If we're in combined view, turn it off first
    if (combinedViewMode) {
      handleCombinedViewToggle(false);
    }

    // Then change the mode
    onModeChange(VIEWS.READER);
  };

  const handleAnswersClick = () => {
    // If we're in combined view, turn it off first
    if (combinedViewMode) {
      handleCombinedViewToggle(false);
    }

    // Then change the mode
    onModeChange(VIEWS.ANSWERS);
  };

  return (
    <Box className={classes.container}>
      <Box
        className={classes.tabsContainer}
        role="tablist"
        aria-label="Content view options">
        <Button
          className={clsx(
            classes.tab,
            isHighlightsActive && [classes.activeTab, classes.leftButton]
          )}
          onClick={handleHighlightsClick}
          disableElevation
          variant={isHighlightsActive ? "contained" : "text"}
          role="tab"
          aria-selected={isHighlightsActive}
          aria-controls="highlights-panel"
          id="highlights-tab">
          Text
        </Button>
        <Button
          className={clsx(
            classes.tab,
            isAnswersActive && [classes.activeTab, classes.rightButton]
          )}
          onClick={handleAnswersClick}
          disableElevation
          variant={isAnswersActive ? "contained" : "text"}
          role="tab"
          aria-selected={isAnswersActive}
          aria-controls="answers-panel"
          id="answers-tab">
          Answer
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(TabNavigation);
